import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";

import { StrategyEx } from "@front/m19-models";
import { AccountSelectionService } from "@front/m19-services";
import {
  IButtonDirective,
  ISelectMenuImports,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { map, switchMap, take } from "rxjs/operators";
import { TranslocoDirective } from "@jsverse/transloco";
import { StrategyStateComponent } from "@m19-board/shared/strategy-state/strategy-state.component";
import { SdStrategyCreationComponent } from "@m19-board/sponsored-display/sd-manager/sd-strategy-creation/sd-strategy-creation.component";
import { SpStrategyGroupCreationComponent } from "@m19-board/sponsored-product/sp-creation/sp-strategy-creation/sp-strategy-group-creation.component";
import { CampaignType } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { SpStrategyCreationModalData } from "@m19-board/sponsored-product/sp-page/sp-substrategy-creation/sp-strategy-creation.component";

const NEW_STRATEGY = "new_strategy" as const;

@UntilDestroy()
@Component({
  selector: "app-move-strategy-asins-modal",
  templateUrl: "./move-strategy-asins-modal.component.html",
  imports: [TranslocoDirective, ISelectMenuImports, IButtonDirective, StrategyStateComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoveStrategyAsinsModalComponent implements OnInit {
  private readonly toasterService = inject(ToasterService);
  private readonly modalService = inject(ModalService);
  private strategyService = inject(StrategyService);
  public modalRef = inject(ModalRef);
  private accountSelectionService = inject(AccountSelectionService);
  protected readonly modalData = inject<MoveStrategyAsinsModalData>(MODAL_DATA);

  protected readonly NEW_STRATEGY = NEW_STRATEGY;

  strategies = signal<StrategyEx[]>([]);
  searchQuery = signal("");
  target = signal<StrategyEx | typeof NEW_STRATEGY>(NEW_STRATEGY);

  filteredStrategies = computed(() => {
    const strategies = this.strategies();
    const query = this.searchQuery().toLowerCase();
    if (!query) return strategies;
    return strategies.filter((s) => (s.name ?? "").toLowerCase().includes(query));
  });

  ngOnInit(): void {
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        switchMap((am) => this.strategyService.getStrategies(am.accountId, am.marketplace)),
        untilDestroyed(this),
        map((strategyIndex) => Array.from(strategyIndex.values())),
      )
      .subscribe((strategies) => {
        const filtered = strategies.filter(
          (s) =>
            s.campaignType == this.modalData.source.campaignType &&
            !s.isSpStrategyGroup() &&
            !s.defaultStrategy &&
            !s.asins.find((a) => this.modalData.asinsToMove.includes(a.asin)),
        );
        filtered.sort((a, b) => (a.state === "ENABLED" ? -1 : 1) - (b.state === "ENABLED" ? -1 : 1));
        this.strategies.set(filtered);
        this.target.set(NEW_STRATEGY);
      });
  }

  moveAsinsToStrategy(target: StrategyEx) {
    this.strategyService
      .moveAsinsToStrategy(this.modalData.asinsToMove, this.modalData.source, target)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toasterService.success("ASINs succesfully moved to other strategy");
        },
        error: (error: string) => {
          this.toasterService.error(`Error moving ASINs to other strategy: ${error}`, "Error moving ASINS");
        },
      });
  }

  createStrategy() {
    const modalOptions: ModalOptions<SpStrategyCreationModalData> = {
      size: "lg",
      height: "fill",
      padding: "none",
      overflowY: "hidden",
      data: {
        asins: this.modalData.asinsToMove,
      },
    };

    const creationModalRef =
      this.modalData.source.campaignType === CampaignType.SD
        ? this.modalService.openModal(SdStrategyCreationComponent, modalOptions)
        : this.modalService.openModal(SpStrategyGroupCreationComponent, modalOptions);

    creationModalRef
      .afterClosed()
      .pipe(
        switchMap(() =>
          this.strategyService.deleteAsinsFromStrategy(this.modalData.source, this.modalData.asinsToMove),
        ),
        take(1),
      )
      .subscribe({
        next: () => {
          this.toasterService.success("ASINs succesfully moved to new strategy");
        },
        error: (error: string) => {
          this.toasterService.error(`Error moving ASINs to new strategy: ${error}`, "Error moving ASINS");
        },
      });
  }

  prettyPrintAsins(asins: string[]): string {
    const maxAsins = 5;
    let message = asins.slice(0, maxAsins).join(", ");
    if (asins.length > maxAsins) {
      message += `… (and ${asins.length - maxAsins} more)`;
    }
    return message;
  }

  selectStrategy(strategy: StrategyEx | typeof NEW_STRATEGY | undefined): void {
    if (strategy == null) return;
    this.target.set(strategy);
  }

  confirm(): void {
    const target = this.target();
    if (target === NEW_STRATEGY) {
      this.createStrategy();
    } else {
      this.moveAsinsToStrategy(target);
    }
    this.modalRef.close();
  }
}

export interface MoveStrategyAsinsModalData {
  source: StrategyEx;
  asinsToMove: string[];
}
